import { FunctionComponent } from 'preact';
import { styled } from 'goober';
import { useConfig } from '../providers/ConfigProvider';
import { useUnifiedWidget } from '../providers/UnifiedWidgetProvider';
import { forwardRef } from 'preact/compat';
import { ChatBot } from '../icons/Chat';
import VoiceIcon from '../icons/Voice';
interface MenuContainerProps {
  open: boolean;
  position: string;
  width: number;
}

const MenuContainer = styled('div', forwardRef)(({
  open,
  position,
  width,
}: MenuContainerProps) => ({
  position: "absolute",
  bottom: 0,
  width: `${width}px`,
  maxWidth: "100%",
  right: position === "bottom-right" ? '0' : undefined,
  left: position === "bottom-left" ? '0' : undefined,
  borderRadius: "16px",
  overflow: "hidden",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
  visibility: open ? "visible" : "hidden",
  opacity: open ? 1 : 0,
  transform: open ? "none" : "translateY(20px) scale(0.95)",
  transition: `opacity 0.2s ease-in-out, transform 0.2s ease-in-out${open ? "" : ", max-width 0s linear 0.2s"}`,
  maxHeight: open ? "100%" : "0px",
  pointerEvents: open ? "auto" : "none",
  background: '#ffffff',
}));

const MenuContent = styled('div')`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MenuTitle = styled('h3')`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  text-align: center;
`;

const MenuSubtitle = styled('p')`
  margin: 0;
  font-size: 14px;
  text-align: center;
  opacity: 0.8;
`;

const ModeButton = styled('button')<{ 
  variant: 'primary' | 'secondary',
  color: string,
  buttonColor: string,
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 14px 20px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  background: ${({ buttonColor }) => buttonColor};
  color: ${({ color }) => color};
  opacity: 0.95;

  &:hover {
    transform: scale(1.02);
    opacity: 1;
    background: ${({ buttonColor }) => buttonColor};
  }

  &:active {
    transform: translateY(0);
    background: ${({ buttonColor }) => buttonColor};
  }
`;

const ButtonText = styled('span')`
  flex: 1;
  font-size: 14px;
  text-align: left;
`;

const ButtonDescription = styled('div')<{ color: string }>`
  font-size: 12px;
  font-weight: 400;
  color: ${({ color }) => color};
  margin-top: 4px;
  opacity: 0.8;
`;

export const ModeSelectionMenu: FunctionComponent = () => {
  const { config } = useConfig();
  const { open, mode, setMode } = useUnifiedWidget();
  const { position, width, chatOptions } = config;

  const isMenuMode = mode === 'menu';
  const showMenu = open && isMenuMode;
  
  // Determine which options to show based on chatOptions config
  const showChatOption = chatOptions === 'chat' || chatOptions === 'chat_and_voice';
  const showVoiceOption = chatOptions === 'voice' || chatOptions === 'chat_and_voice';

  return (
    <MenuContainer open={showMenu} position={position} width={width}>
      <MenuContent>
        { config.titleText && <MenuTitle>{config.titleText}</MenuTitle> }
        { config.subtitleText && <MenuSubtitle>{config.subtitleText}</MenuSubtitle> }
        
        {showChatOption && (
          <ModeButton
            onClick={() => setMode('chat')}
            variant="primary"
            color={config.buttonTextColor}
            buttonColor={config.primaryColor}
            aria-label="Start text chat"
          >
            <ChatBot fill={config.buttonTextColor} width={28} height={28} />
            <ButtonText>
              {config.chatButtonText}
              <ButtonDescription color={config.buttonTextColor}>
                {config.chatButtonDescriptionText}
              </ButtonDescription>
            </ButtonText>
          </ModeButton>
        )}

        {showVoiceOption && (
          <ModeButton
            onClick={() => setMode('voice')}
            variant={showChatOption ? 'secondary' : 'primary'}
            color={config.buttonTextColor}
            buttonColor={config.primaryColor}
            aria-label="Start voice call"
          >
            <VoiceIcon fill={config.buttonTextColor} width={28} height={28} />
            <ButtonText>
              {config.voiceButtonText}
              <ButtonDescription color={config.buttonTextColor}>
                {config.voiceButtonDescriptionText}
              </ButtonDescription>
            </ButtonText>
          </ModeButton>
        )}
      </MenuContent>
    </MenuContainer>
  );
};

